import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AppError, AppErrorKind } from '../errors/app-error';
import { ToastService } from '../services/toast.service';
import { LanguageService } from '../services/language.service';
import { AuthService } from '../services/auth.service';
import {
  httpErrorMessage,
  looksLikeTechnicalServerMessage,
  resolveServerFailureToastMessage,
} from '../i18n/http-error-messages';
import { ApiResponse } from '../models/api-response';
import { SUPPRESS_AUTH_REDIRECT_HEADER, SUPPRESS_TOAST_HEADER } from './http-context';

interface MaybeApiBody {
  message?: string;
  errors?: string[] | Record<string, string[]>;
  success?: boolean;
}

function statusToKind(status: number): AppErrorKind {
  if (status === 0) return 'network';
  if (status === 413) return 'validation';
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 422 || status === 400) return 'validation';
  if (status === 429) return 'rateLimit';
  if (status >= 500) return 'server';
  return 'unknown';
}

function kindToKey(kind: AppErrorKind): string {
  switch (kind) {
    case 'network': return 'errors.network';
    case 'auth': return 'errors.sessionExpired';
    case 'forbidden': return 'errors.forbidden';
    case 'notFound': return 'errors.notFound';
    case 'conflict': return 'errors.conflict';
    case 'validation': return 'errors.validation';
    case 'rateLimit': return 'errors.tooMany';
    case 'server': return 'errors.server';
    default: return 'errors.unknown';
  }
}

function looksLikeRawAngularHttp(msg: string | undefined): boolean {
  if (!msg) return false;
  return /^Http failure response for/i.test(msg) || /^Http failure during parsing/i.test(msg);
}

function extractServerMessage(body: unknown): {
  message?: string;
  fieldErrors?: Record<string, string[]>;
  errorList?: string[];
} {
  if (typeof body === 'string') {
    return { message: looksLikeRawAngularHttp(body) ? undefined : body };
  }
  if (typeof body !== 'object' || body == null) return {};
  const b = body as MaybeApiBody;
  let fieldErrors: Record<string, string[]> | undefined;
  let errorList: string[] | undefined;
  if (b.errors && !Array.isArray(b.errors) && typeof b.errors === 'object') {
    fieldErrors = b.errors as Record<string, string[]>;
  }
  if (Array.isArray(b.errors) && b.errors.length) {
    errorList = (b.errors as string[]).map(e => String(e).trim()).filter(Boolean);
  }
  let message = typeof b.message === 'string' && b.message.trim() ? b.message.trim() : undefined;
  if (!message && errorList?.length) {
    message = errorList.join(' — ');
  }
  return { message, fieldErrors, errorList };
}

function isAuthEndpoint(req: HttpRequest<unknown>): boolean {
  const u = req.url.toLowerCase();
  return /\/auth\/(login|register|social-login|forgot-password|reset-password|verify-email|verify-email\/session|resend-verification-email)(\b|$|\?)/.test(u);
}

function shouldRedirectOn401(req: HttpRequest<unknown>): boolean {
  if (req.headers.has(SUPPRESS_AUTH_REDIRECT_HEADER)) return false;
  if (isAuthEndpoint(req)) return false;
  return true;
}

function stripSentinelHeaders<T>(req: HttpRequest<T>): HttpRequest<T> {
  let headers = req.headers;
  if (headers.has(SUPPRESS_TOAST_HEADER)) headers = headers.delete(SUPPRESS_TOAST_HEADER);
  if (headers.has(SUPPRESS_AUTH_REDIRECT_HEADER)) headers = headers.delete(SUPPRESS_AUTH_REDIRECT_HEADER);
  return req.clone({ headers });
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toast = inject(ToastService);
  const language = inject(LanguageService);
  const router = inject(Router);
  const auth = inject(AuthService);

  const suppressToast = req.headers.has(SUPPRESS_TOAST_HEADER);
  const allowAuthRedirect = shouldRedirectOn401(req);

  const cleanReq = stripSentinelHeaders(req);

  return next(cleanReq).pipe(
    catchError(err => {
      if (err instanceof AppError) {
        return throwError(() => err);
      }

      const he = err instanceof HttpErrorResponse ? err : null;
      const status = he?.status ?? 0;
      const kind = statusToKind(status);
      const key = kindToKey(kind);

      const body = he?.error;
      const apiResponseBody = (body && typeof body === 'object' && 'success' in body)
        ? (body as ApiResponse<unknown>)
        : null;
      const isApiFailureBody = apiResponseBody?.success === false;

      const { message: serverMessage, fieldErrors, errorList } = extractServerMessage(body);

      const fallbackTranslated = httpErrorMessage(key, language.currentLang);
      const lang = language.currentLang;

      let userMessage = fallbackTranslated;
      const isServerError = status >= 500;
      const safeErrorList = errorList?.filter(e => !looksLikeTechnicalServerMessage(e));
      const safeServerMessage =
        serverMessage && !looksLikeRawAngularHttp(serverMessage) && !looksLikeTechnicalServerMessage(serverMessage)
          ? serverMessage
          : undefined;

      if (isServerError) {
        userMessage = httpErrorMessage('errors.server', lang);
      } else if (safeErrorList?.length) {
        const parts = safeErrorList
          .map(e => resolveServerFailureToastMessage(e, lang))
          .filter(Boolean);
        if (parts.length) userMessage = parts.join(' — ');
      } else if (safeServerMessage) {
        if (isApiFailureBody || (status >= 400 && status < 500 && status !== 401)) {
          userMessage = resolveServerFailureToastMessage(safeServerMessage, lang);
        }
      }
      if (kind === 'auth') {
        userMessage = fallbackTranslated;
      }
      if (status === 413) {
        userMessage = httpErrorMessage('errors.fileTooLarge', lang);
      }

      const appError = new AppError(kind, status, key, userMessage, serverMessage, fieldErrors, body);

      let didRedirectToLogin = false;
      if (kind === 'auth' && allowAuthRedirect) {
        try {
          const currentUrl = router.url;
          const isOnLogin = currentUrl.startsWith('/login');
          auth.clearSession();
          if (!isOnLogin) {
            const returnUrl = currentUrl && currentUrl !== '/' ? currentUrl : undefined;
            router.navigate(['/login'], returnUrl ? { queryParams: { returnUrl } } : undefined);
            didRedirectToLogin = true;
          }
        } catch { /* navigation best-effort */ }
      }

      // 401 always toasts when we redirect — the user must understand why the
      // page just changed. Other errors respect the suppress flag so inline
      // form errors don't get duplicated.
      const shouldToast = didRedirectToLogin ? true : !suppressToast;
      if (shouldToast) {
        toast.error(userMessage);
      }

      return throwError(() => appError);
    })
  );
};
